import { useTranslation } from "react-i18next";
import { useEffect, useRef, useState } from "react";
import { useParallax } from "@/hooks/useParallax";

const partnersRow1 = [
  { name: "Palo Alto Networks", icon: "ri-shield-flash-line" },
  { name: "Fortinet", icon: "ri-shield-check-line" },
  { name: "Cisco Secure", icon: "ri-global-line" },
  { name: "Check Point", icon: "ri-lock-line" },
  { name: "CrowdStrike", icon: "ri-crosshair-line" },
  { name: "Splunk", icon: "ri-bar-chart-box-line" },
  { name: "Microsoft Security", icon: "ri-windows-line" },
  { name: "AWS Security", icon: "ri-cloud-line" },
  { name: "Google Cloud", icon: "ri-google-line" },
  { name: "IBM Security", icon: "ri-cpu-line" },
];

const partnersRow2 = [
  { name: "Trend Micro", icon: "ri-virus-line" },
  { name: "Kaspersky", icon: "ri-shield-keyhole-line" },
  { name: "Sophos", icon: "ri-shield-star-line" },
  { name: "F5 Networks", icon: "ri-server-line" },
  { name: "Proofpoint", icon: "ri-mail-check-line" },
  { name: "CyberArk", icon: "ri-key-2-line" },
  { name: "McAfee", icon: "ri-shield-user-line" },
  { name: "VMware", icon: "ri-database-2-line" },
  { name: "Veeam", icon: "ri-hard-drive-3-line" },
  { name: "SentinelOne", icon: "ri-radar-line" },
];

function PartnerBadge({ name, icon }: { name: string; icon: string }) {
  return (
    <div className="group flex items-center gap-2.5 px-5 py-3 rounded-lg border border-background-200/60 bg-white mx-2 md:mx-3 flex-shrink-0 hover:border-primary-300 transition-all duration-400 cursor-default select-none">
      <div className="w-8 h-8 flex items-center justify-center rounded-md bg-primary-500/10 text-primary-400 group-hover:bg-primary-500/20 group-hover:text-primary-300 transition-all duration-300">
        <i className={`${icon} text-sm`} />
      </div>
      <span className="font-heading font-semibold text-sm text-foreground-800 group-hover:text-primary-600 transition-colors duration-300 whitespace-nowrap">
        {name}
      </span>
    </div>
  );
}

export default function CertificacionesAlianzas() {
  const { t } = useTranslation("common");
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const { ref: bgRef, offset } = useParallax(0.06);

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      id="certificaciones"
      ref={sectionRef}
      className="relative py-10 md:py-14 overflow-hidden"
    >
      {/* Humanized partnership background with subtle parallax */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          ref={bgRef}
          className="absolute -top-[10%] left-0 w-full h-[120%] opacity-45"
          style={{ transform: `translateY(${offset}px)` }}
        >
          <img
            src="https://readdy.ai/api/search-image?query=Business%20professionals%20shaking%20hands%20in%20a%20modern%20corporate%20office%20lobby%2C%20partnership%20and%20trust%20concept%2C%20diverse%20team%20in%20background%2C%20warm%20natural%20daylight%2C%20professional%20editorial%20photography%2C%20soft%20depth%20of%20field%2C%20human%20centered&width=1600&height=600&seq=cert-human-bg-01&orientation=landscape"
            srcSet="https://readdy.ai/api/search-image?query=Business%20professionals%20shaking%20hands%20in%20a%20modern%20corporate%20office%20lobby%2C%20partnership%20and%20trust%20concept%2C%20diverse%20team%20in%20background%2C%20warm%20natural%20daylight%2C%20professional%20editorial%20photography%2C%20soft%20depth%20of%20field%2C%20human%20centered%201280wquery=Business%20professionals%20shaking%20hands%20in%20a%20modern%20corporate%20office%20lobby%2C%20partnership%20and%20trust%20concept%2C%20diverse%20team%20in%20background%2C%20warm%20natural%20daylight%2C%20professional%20editorial%20photography%2C%20soft%20depth%20of%20field%2C%20human%20centered%20640w%2C%20https:%2F%2Freaddy.ai%2Fapi%2Fsearch-image%3Fwidth=1280&width=640&height=240&seq=cert-human-bg-01&orientation=landscape&height=480&seq=cert-human-bg-01&orientation=landscape"
            sizes="100vw"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-center"
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="absolute inset-0 bg-secondary-950/78" />
        <div className="absolute inset-0 bg-gradient-to-b from-secondary-950 via-secondary-950/55 to-secondary-950" />
      </div>

      {/* Radial glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-primary-500/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="relative z-10 w-full px-4 md:px-6">
        {/* Header */}
        <div
          className={`text-center mb-6 md:mb-8 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <p className="font-body text-xs text-primary-400 font-medium uppercase tracking-widest mb-1.5">
            {t("cert.tag")}
          </p>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2">
            {t("cert.title")} <span className="text-primary-400">{t("cert.highlight")}</span>
          </h2>
          <p className="font-body text-sm text-white/70 max-w-2xl mx-auto leading-relaxed">
            {t("cert.subtitle")}
          </p>
        </div>

        {/* Row 1 - scroll left */}
        <div
          className={`relative mb-4 transition-all duration-1000 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="absolute left-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-r from-secondary-950 to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-l from-secondary-950 to-transparent z-10 pointer-events-none" />
          <div className="flex animate-scroll-left hover:[animation-play-state:paused]">
            {[...partnersRow1, ...partnersRow1].map((partner, idx) => (
              <PartnerBadge key={`r1-${idx}`} name={partner.name} icon={partner.icon} />
            ))}
          </div>
        </div>

        {/* Row 2 - scroll right */}
        <div
          className={`relative transition-all duration-1000 delay-400 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="absolute left-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-r from-secondary-950 to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-16 md:w-24 bg-gradient-to-l from-secondary-950 to-transparent z-10 pointer-events-none" />
          <div className="flex animate-scroll-right hover:[animation-play-state:paused]">
            {[...partnersRow2, ...partnersRow2].map((partner, idx) => (
              <PartnerBadge key={`r2-${idx}`} name={partner.name} icon={partner.icon} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}